<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import {
    formatDateLong,
    formatTime,
  } from '@/domain/reservations/reservation-time';
  import type { IReservationEvent } from '@/interfaces/IReservation';
  const props = withDefaults(
    defineProps<{
      events: IReservationEvent[];
      timeZone: string;
      loading?: boolean;
    }>(),
    { loading: false }
  );
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const ordered = computed(() =>
    [...props.events].sort(
      (a, b) => b.createdAt.localeCompare(a.createdAt) || b.id - a.id
    )
  );
  function text(event: IReservationEvent) {
    const data = event.data ?? {};
    const value = (key: string): string => {
      const field = data[key];
      return typeof field === 'string' ? field : '';
    };
    const status = value('to') || value('status');
    const template = value('template');
    const channel = value('channel');
    if (event.type === 'note' && value('message')) return value('message');
    return t(`reservations.event.${event.type}`, {
      status: proxy.$te(`reservations.status.${status}`)
        ? t(`reservations.status.${status}`)
        : status,
      template: proxy.$te(`reservations.template.${template}`)
        ? t(`reservations.template.${template}`)
        : template,
      channel: proxy.$te(`reservations.channel.${channel}`)
        ? t(`reservations.channel.${channel}`)
        : channel,
      text: value('text'),
      minutes: typeof data.minutes === 'number' ? data.minutes : '',
    });
  }
  function actor(event: IReservationEvent) {
    return (
      event.actorName ||
      t(
        event.actor === 'staff'
          ? 'reservations.source.staff'
          : `reservations.event.actor.${event.actor}`
      )
    );
  }
  function date(event: IReservationEvent) {
    return `${formatDateLong(
      event.createdAt,
      props.timeZone,
      proxy.$i18n.locale
    )} · ${formatTime(event.createdAt, props.timeZone, proxy.$i18n.locale)}`;
  }
</script>

<template>
  <div class="reservation-events">
    <div v-if="loading && !events.length" class="event-loading">
      <PosUiSkeleton v-for="n in 3" :key="n" class="event-skeleton" />
    </div>
    <ol v-else-if="events.length">
      <li v-for="event in ordered" :key="event.id">
        <span
          class="event-dot"
          :class="{
            failed:
              event.type === 'message_failed' ||
              event.type === 'deposit_refund_failed',
          }"
        /><time :datetime="event.createdAt">{{ date(event) }}</time>
        <p>
          <strong>{{ actor(event) }}</strong> · {{ text(event) }}
        </p>
      </li>
    </ol>
    <PosUiEmptyState
      v-else
      icon="history"
      :title="$t('club.member.activityEmpty')"
    />
  </div>
</template>

<style scoped lang="scss">
  .reservation-events {
    .event-loading {
      @apply tw-flex tw-flex-col tw-gap-3;
    }
    .event-skeleton {
      @apply tw-h-12;
    }
    ol {
      @apply tw-m-0 tw-list-none tw-p-0;
    }
    li {
      @apply tw-relative tw-border-l tw-border-neutral-200 tw-pb-5 tw-pl-4;
      &:last-child {
        @apply tw-pb-0;
      }
    }
    .event-dot {
      @apply tw-absolute tw-left-[-4px] tw-top-1.5 tw-h-2 tw-w-2 tw-rounded-full tw-bg-neutral-300;
      &.failed {
        @apply tw-bg-red-400;
      }
    }
    time {
      @apply tw-text-xs tw-text-neutral-400;
    }
    p {
      @apply tw-mt-1 tw-break-words tw-text-sm tw-text-neutral-600;
      strong {
        @apply tw-font-medium tw-text-neutral-800;
      }
    }
  }
</style>
